import { Injectable } from '@angular/core';
import { LoadingController } from '@ionic/angular';
import { ToastController } from '@ionic/angular';

import { languageService } from './language';

export type toastType = 'primary' | 'success' | 'warning' | 'danger';

/**
 * @class toastService
 * @description A service to handles displaying toast notifications and loading indicators
 */
@Injectable({
    providedIn: 'root'
})
export class toastService {
    private _waiting = false;
    private _loading: any = null;
    
    /**
     * Creates an instance of the `toastService`.
     * 
     * @param lang - The language service for retrieving localized strings.
     * @param toast - The Ionic `ToastController` for displaying toast notifications.
     * @param loadingCtr - The Ionic `LoadingController` for displaying loading indicators.
     */    
    constructor(private lang: languageService, public toast: ToastController, public loadingCtr: LoadingController) {
        // nothing to do
    }
    
    /**
     * Displays a loading indicator with an optional custom message.
     * 
     * @param message - (Optional) The message to display. If not provided, the service will use the localized message for `"@loading_wait"`.
     * @param timeout - (Optional) Duration for how long the loading indicator should be visible (in milliseconds).
     *   Defaults to 60000ms so the overlay outlives typical HTTP/cluster round-trips unless `HideWait()` runs first.
     * @returns A Promise that resolves once the loading indicator is presented.
     */    
    async ShowWait(message: string | null = null, timeout = 60000){
        if (this._loading){
            return;
        }
        
        this._waiting = true;
        this._loading = await this.loadingCtr.create({
            duration: timeout,
            message: this.lang.tr(message || "@loading_wait"),
            translucent: true
        });        

        await this._loading.present();
        if (!this._waiting){
            this.HideWait();
        }
    }
    
    /**
     * Dismisses the currently visible loading indicator after a short delay.
     */    
    HideWait(){
        setTimeout(async () => {
            if (this._loading){
                (document.activeElement as HTMLElement)?.blur();
                await this._loading.dismiss();
            }

            this._loading = null;
            this._waiting = false;
        }, 500);
    }
    
    /**
     * Displays a toast notification.
     * 
     * @param type - The type of the toast. It determines the color and style of the toast. 
     *               Accepts `'primary'`, `'success'`, `'warning'`, or `'danger'`.
     * @param text - The message to display in the toast notification.
     * @param duration - (Optional) The duration the toast should be visible (in milliseconds). Defaults to 5000ms.
     * @returns A Promise that resolves once the toast is presented.
     */    
    async ShowAlert(type: toastType, text: string, duration = 5000) {
        const t = await this.toast.create({
            color: type,
            message: text,
            duration: duration,
            position: 'bottom',
        });
        
        t.present();      
    }
}
